import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import AssetForm from '../components/asset/AssetForm';
import Button from '../components/ui/Button';
import { loadAssets, saveAssets, generateId, loadAssetTypes } from '../utils/storage';

const AssetFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [asset, setAsset] = useState(null);
  const [assetTypes, setAssetTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  // If pathname is /asset/new, it's new mode. Otherwise if id exists, it's edit mode
  const isEditMode = location.pathname !== '/asset/new' && id !== undefined;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Load asset types first
        const types = await loadAssetTypes();
        setAssetTypes(types);

        if (isEditMode) {
          // Edit mode - load the asset
          const assets = await loadAssets();
          const found = assets.find(a => a.id === id);
          if (found) {
            setAsset(found);
          } else {
            navigate('/dashboard');
            return;
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, isEditMode, navigate]);

  const handleAutoSave = async (formData) => {
    // For new assets, create the asset so it has an ID for file uploads
    if (!isEditMode && !asset) {
      const assets = await loadAssets();
      const newAsset = {
        id: generateId(),
        ...formData,
        images: formData.images || [],
        documents: formData.documents || [],
        maintenanceRecords: formData.maintenanceRecords || []
      };
      await saveAssets([...assets, newAsset]);
      setAsset(newAsset);
      return newAsset;
    }
    return asset;
  };

  const handleSave = async (formData) => {
    const assets = await loadAssets();
    
    if (isEditMode) {
      const updatedAssets = assets.map(a =>
        a.id === id ? { ...a, ...formData } : a
      );
      await saveAssets(updatedAssets);
    } else {
      // If asset was already created via auto-save, update it; otherwise create new
      if (asset && asset.id) {
        const updatedAssets = assets.map(a =>
          a.id === asset.id ? { ...a, ...formData } : a
        );
        await saveAssets(updatedAssets);
      } else {
        const newAsset = {
          id: generateId(),
          ...formData,
          maintenanceRecords: []
        };
        await saveAssets([...assets, newAsset]);
      }
    }
    
    navigate('/dashboard');
  };

  if (loading && isEditMode) {
    return (
      <>
        <Navbar />
        <div className="pt-24 pb-12 px-4 text-center">Loading...</div>
      </>
    );
  }


  return (
    <>
      <Navbar />
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-6 flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Dashboard</span>
          </Button>

          <div className="glassmorphic p-6 sm:p-8">
            <h1 className="text-2xl font-light text-foreground mb-6">
              {isEditMode ? 'Edit Asset' : 'Add New Asset'}
            </h1>
            {!loading && assetTypes.length > 0 && (
              <AssetForm
                asset={asset}
                onSave={handleSave}
                onAutoSave={handleAutoSave}
                onCancel={() => navigate('/dashboard')}
                assetTypes={assetTypes}
              />
            )}
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default AssetFormPage;

